import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Wind, CloudRain, MapPin, Moon, Sunrise, Sunset, Ribbon,
  ChevronDown, ChevronUp, Gauge, Droplet, Thermometer, Sun,
  Calendar as CalendarIcon, Loader2, CloudOff, RefreshCcw, Navigation2,
} from "lucide-react";

const SLO_LOCATIONS = [
  { name: "Bled",          lat: 46.3683, lon: 14.1146 },
  { name: "Kočevski rog",  lat: 45.6620, lon: 15.0180 },
  { name: "Postojna",      lat: 45.7748, lon: 14.2136 },
  { name: "Pohorje",       lat: 46.4842, lon: 15.4442 },
  { name: "Kranjska Gora", lat: 46.4839, lon: 13.7847 },
  { name: "Krakovski gozd",lat: 45.8500, lon: 15.4167 },
];

// Score → color
function scoreColor(score) {
  if (score == null) return "#6b7280";
  if (score >= 80) return "#2D7D46";
  if (score >= 65) return "#5A9E4B";
  if (score >= 45) return "#D89A1E";
  return "#C0392B";
}

const FACTOR_ICONS = {
  luna:     Moon,
  tlak:     Gauge,
  veter:    Wind,
  padavine: Droplet,
  temp:     Thermometer,
};

// "14. Jul 19:01" → "14. jul 19:01" (short month lowercase, Slovenian)
function niceDate(raw) {
  if (!raw) return "—";
  return raw
    .replace(/\bJan\b/, "jan").replace(/\bFeb\b/, "feb").replace(/\bMar\b/, "mar")
    .replace(/\bApr\b/, "apr").replace(/\bMay\b/, "maj").replace(/\bJun\b/, "jun")
    .replace(/\bJul\b/, "jul").replace(/\bAug\b/, "avg").replace(/\bSep\b/, "sep")
    .replace(/\bOct\b/, "okt").replace(/\bNov\b/, "nov").replace(/\bDec\b/, "dec");
}

export const WeatherHuntingCard = () => {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [coords, setCoords] = useState(null); // {lat, lon, isFallback, name}
  const [expanded, setExpanded] = useState(false);

  const randomFallback = useMemo(
    () => SLO_LOCATIONS[Math.floor(Math.random() * SLO_LOCATIONS.length)],
    []
  );

  // Takoj nastavi fallback lokacijo, da se vremenska kartica pokaže brez čakanja.
  // Geolokacija (če je dovoljena) potem osveži koordinate.
  useEffect(() => {
    setCoords((prev) => prev || { ...randomFallback, isFallback: true });
    if (!navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude, isFallback: false, name: null });
      },
      () => { /* fallback že nastavljen */ },
      { enableHighAccuracy: false, timeout: 3000, maximumAge: 60_000 }
    );
    return () => { cancelled = true; };
  }, [randomFallback]);

  const fetchAll = async () => {
    if (!coords) return;
    setStatus("loading");
    const params = `?lat=${coords.lat}&lon=${coords.lon}`;
    // Fire both requests independently — weather shows immediately, forecast fills in later.
    axios.get(`${API}/weather${params}`)
      .then(r => { setWeather(r.data); setStatus("ready"); })
      .catch(() => setStatus("error"));
    // Forecast (with AI tip) can take up to 6s — do NOT block the weather display.
    // Use with_ai=false for faster first paint; retry with AI in background after.
    axios.get(`${API}/hunting-forecast${params}&with_ai=false`)
      .then(r => setForecast(r.data))
      .catch(() => {})
      .finally(() => {
        // Background enrichment: fetch AI tip separately so hunting index shows fast
        axios.get(`${API}/hunting-forecast${params}&with_ai=true`, { timeout: 10000 })
          .then(r => setForecast(r.data))
          .catch(() => {});
      });
  };

  useEffect(() => { fetchAll(); }, [coords]);

  const requestGeo = () => {
    if (!navigator.geolocation) return;
    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude, isFallback: false, name: null }),
      () => setStatus("ready")
    );
  };

  if (status === "loading" && !weather) {
    return (
      <Card data-testid="weather-hunting-card" className="bg-[#0b3a1e] text-white border-0 shadow-lg">
        <CardContent className="p-6 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Pridobivanje podatkov...</span>
        </CardContent>
      </Card>
    );
  }
  if (status === "error" && !weather) {
    return (
      <Card data-testid="weather-hunting-card" className="bg-slate-100 border border-slate-200">
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700">
            <CloudOff className="h-5 w-5" />
            <span className="text-sm">Vremenski podatki niso dosegljivi.</span>
          </div>
          <Button size="sm" variant="outline" onClick={fetchAll} data-testid="wc-retry">
            <RefreshCcw className="h-3 w-3 mr-1" /> Znova
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!weather) return null;

  const score = forecast?.score;
  const scoreCol = scoreColor(score);
  const stanica = weather.lokacija || (coords?.isFallback ? `Privzeta lokacija: ${coords.name}` : "Moja lokacija");

  return (
    <div className="space-y-3" data-testid="weather-hunting-card">
      {/* ============ 1) WEATHER STRIP (dark green) ============ */}
      <Card className="bg-[#0b3a1e] text-white border-0 shadow-lg overflow-hidden">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* LEFT: icon + temp + condition */}
            <div className="flex items-center gap-3 md:flex-1 min-w-0">
              <div className="text-5xl leading-none">{weather.ikona}</div>
              <div className="min-w-0">
                <div className="text-4xl font-bold leading-tight" data-testid="wc-temp">{weather.temperatura}°C</div>
                <div className="text-sm text-emerald-100/90 truncate">{weather.opis}</div>
                {weather.obcutena_temp != null && (
                  <div className="text-[11px] text-emerald-100/70">Občuteno {weather.obcutena_temp}°C</div>
                )}
              </div>
            </div>

            {/* RIGHT: metrics */}
            <div className="flex items-stretch gap-3">
              <div className="bg-white/10 rounded-lg px-3 py-2 min-w-[100px]" data-testid="wc-wind">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-emerald-100/80">
                  <Navigation2 className="h-3 w-3" /> Veter
                </div>
                <div className="text-base font-semibold">
                  {weather.veter_smer || "—"} {weather.veter_hitrost != null ? `${weather.veter_hitrost} km/h` : "—"}
                </div>
                {weather.vonj_smer && (
                  <div className="text-[10px] text-amber-200/90">Vonj → {weather.vonj_smer}</div>
                )}
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2 min-w-[100px]" data-testid="wc-precip">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-emerald-100/80">
                  <CloudRain className="h-3 w-3" /> Padavine
                </div>
                <div className="text-base font-semibold">
                  {weather.padavine_ura != null ? `${weather.padavine_ura} mm` : "—"}
                </div>
                <div className="text-[10px] text-emerald-100/70">
                  Vlažnost {weather.vlaznost}%
                </div>
              </div>
            </div>
          </div>

          {/* Moon row + navtična zora/mrak */}
          {(weather.luna_faza || forecast?.best_times?.dawn || forecast?.best_times?.dusk) && (
            <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              {weather.luna_faza && (
                <div className="flex items-center gap-2">
                  <span className="text-lg">{weather.luna_ikona}</span>
                  <span className="font-medium">
                    Luna: {weather.luna_faza} · {weather.luna_osvetljenost}%
                  </span>
                  {weather.luna_naslednja_faza && (
                    <span className="text-emerald-100/70 text-xs">
                      · {weather.luna_naslednja_faza}: {niceDate(weather.luna_naslednja_cas)}
                    </span>
                  )}
                </div>
              )}
              {forecast?.best_times?.dawn && (
                <div className="flex items-center gap-1" title="Navtična zora – sonce 12° pod obzorjem">
                  <Sunrise className="h-4 w-4 text-amber-300" />
                  <span className="text-emerald-100/90 text-xs uppercase tracking-wide">Navt. zora</span>
                  <span className="font-semibold">{forecast.best_times.dawn}</span>
                </div>
              )}
              {forecast?.best_times?.dusk && (
                <div className="flex items-center gap-1" title="Navtični mrak – sonce 12° pod obzorjem">
                  <Sunset className="h-4 w-4 text-indigo-300" />
                  <span className="text-emerald-100/90 text-xs uppercase tracking-wide">Navt. mrak</span>
                  <span className="font-semibold">{forecast.best_times.dusk}</span>
                </div>
              )}
            </div>
          )}

          {/* Location footer */}
          <div className="mt-2 flex items-center justify-between text-[11px] text-emerald-100/80">
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              <span>{stanica} · Vir: Open-Meteo</span>
            </div>
            {coords?.isFallback && (
              <button
                onClick={requestGeo}
                className="underline hover:text-white"
                data-testid="wc-use-my-location"
              >
                Moja lokacija
              </button>
            )}
          </div>

          {/* Tomorrow forecast – embedded in weather card */}
          {forecast?.tomorrow && (forecast.tomorrow.morning || forecast.tomorrow.evening) && (
            <div className="mt-4 pt-3 border-t border-white/10" data-testid="tomorrow-inline">
              <div className="flex items-center gap-2 mb-2 text-emerald-100/80 text-xs uppercase tracking-wide">
                <CalendarIcon className="h-3 w-3" />
                <span>Napoved za jutri</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TomorrowSlot
                  slot={forecast.tomorrow.morning}
                  kicker="Jutri zjutraj"
                  Icon={Sunrise}
                  accent="text-amber-200"
                  testId="tomorrow-morning"
                  dark
                />
                <TomorrowSlot
                  slot={forecast.tomorrow.evening}
                  kicker="Jutri zvečer"
                  Icon={Sunset}
                  accent="text-indigo-200"
                  testId="tomorrow-evening"
                  dark
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ 2) HUNTING INDEX CARD ============ */}
      {forecast && (
        <Card className="border border-slate-200 bg-white" data-testid="hunting-index-card">
          <button
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition"
            onClick={() => setExpanded((e) => !e)}
            data-testid="hunting-index-toggle"
          >
            <div className="flex items-center gap-2">
              <Ribbon className="h-5 w-5" style={{ color: scoreCol }} />
              <span className="font-semibold text-slate-800">Uspešnost lova</span>
              <span className="text-xs text-slate-500 hidden sm:inline">· {forecast.kategorija}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: scoreCol }} data-testid="hunting-score">
                {forecast.score}%
              </span>
              {expanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
            </div>
          </button>

          {expanded && (
            <div className="px-4 pb-4 space-y-3">
              {(forecast.factors || []).map((f) => {
                const Ic = FACTOR_ICONS[f.key] || Sun;
                return (
                  <div key={f.key} data-testid={`factor-${f.key}`}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Ic className="h-4 w-4" style={{ color: scoreColor(f.score) }} />
                        <span>{f.label}</span>
                      </div>
                      <span className="text-slate-600">{f.value}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${f.score}%`, background: scoreColor(f.score) }}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Best times */}
              {(forecast.best_times?.dawn || forecast.best_times?.dusk) && (
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm bg-amber-50 rounded-md px-3 py-2" data-testid="best-time-dawn" title="Navtična zora — sonce je 12° pod obzorjem">
                    <Sunrise className="h-4 w-4 text-amber-600" />
                    <span className="text-slate-700">Navtična zora <strong>{forecast.best_times.dawn || "—"}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-indigo-50 rounded-md px-3 py-2" data-testid="best-time-dusk" title="Navtični mrak — sonce je 12° pod obzorjem">
                    <Sunset className="h-4 w-4 text-indigo-600" />
                    <span className="text-slate-700">Navtični mrak <strong>{forecast.best_times.dusk || "—"}</strong></span>
                  </div>
                </div>
              )}

              {/* AI tip */}
              {forecast.tip && (
                <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900" data-testid="hunting-tip">
                  💡 {forecast.tip}
                </div>
              )}

              <p className="text-[11px] text-slate-500">
                Ocena temelji na luni, tlaku, vetru, padavinah in temperaturi.
              </p>
            </div>
          )}
        </Card>
      )}

      {/* ============ 3) (Napoved za jutri je zdaj vgrajena v vremensko kartico zgoraj) ============ */}
    </div>
  );
};

const TomorrowSlot = ({ slot, kicker, Icon, accent, testId, dark = false }) => {
  const emptyClass = dark ? "text-emerald-100/50" : "text-slate-400";
  const kickerText = dark ? accent : accent;
  const tempClass = dark ? "text-white" : "text-slate-800";
  const condClass = dark ? "text-emerald-100/80" : "text-slate-600";
  const metaClass = dark ? "text-emerald-100/70" : "text-slate-600";

  if (!slot) {
    return (
      <div className={`text-sm ${emptyClass}`} data-testid={testId}>
        <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium ${kickerText}`}>
          <Icon className="h-3 w-3" /> {kicker}
        </div>
        <div>—</div>
      </div>
    );
  }
  return (
    <div data-testid={testId}>
      <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium ${kickerText}`}>
        <Icon className="h-3 w-3" /> {kicker} · {slot.time}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-2xl">{slot.icon}</span>
        <div>
          <div className={`text-lg font-bold ${tempClass}`}>{slot.temp}°C</div>
          <div className={`text-[11px] ${condClass}`}>{slot.condition}</div>
        </div>
      </div>
      <div className={`flex items-center gap-3 mt-1 text-[11px] ${metaClass}`}>
        <span className="flex items-center gap-1"><Wind className="h-3 w-3" />{slot.wind} km/h</span>
        <span className="flex items-center gap-1"><CloudRain className="h-3 w-3" />{slot.precip_prob}%</span>
      </div>
    </div>
  );
};

export default WeatherHuntingCard;
